import React, { CSSProperties, memo } from 'react';

import { useAppSelector } from 'state/hooks';

import { isMac } from 'appUtils';
import { classNames } from 'features/common/utils';
import { BaseNoSegmentSelectedPanel } from 'features/workspace/NoSegmentSelectedPanel';

import { selectRouteSelection } from '../../../state/workflowSlice';
import imgDesktop1 from './assets/instructions_desktop_1.png';
import imgDesktop2 from './assets/instructions_desktop_2.png';
import imgDesktop3 from './assets/instructions_desktop_3.png';
import imgDesktop4 from './assets/instructions_desktop_4.png';
import imgMac2 from './assets/instructions_desktop_mac_2.png';
import imgMac3 from './assets/instructions_desktop_mac_3.png';
import imgMac4 from './assets/instructions_desktop_mac_4.png';
import imgMobile1 from './assets/instructions_mobile_1.png';
import imgMobile2 from './assets/instructions_mobile_2.png';
import imgMobile3 from './assets/instructions_mobile_3.png';

import styles from './routeInstructions.module.css';

export function RouteInstructionsPanel({
  sx,
  closeBtn,
  maximizeBtn,
}: {
  sx?: CSSProperties;
  closeBtn?: boolean;
  maximizeBtn?: boolean;
}) {
  return (
    <BaseNoSegmentSelectedPanel
      sx={sx}
      closeBtn={closeBtn}
      maximizeBtn={maximizeBtn}
    >
      <RouteInstructions
        extraStyles={{ maxWidth: '60vw', minWidth: '300px' }}
      />
    </BaseNoSegmentSelectedPanel>
  );
}

type State = 'none' | 'one' | 'many';

const activeIndexByState: Record<State, number> = {
  none: 0,
  one: 1,
  many: 2,
};

type ImgDef = { src: string; alt: string };

const STEPS = ['Select an Origin', 'Add a Waypoint', 'Repeat', 'Undo step'];

function toImgDefs(sources: string[]): ImgDef[] {
  return sources.map((src, i) => ({ src, alt: STEPS[i] }));
}

const DESKTOP_IMGS = toImgDefs([
  imgDesktop1,
  imgDesktop2,
  imgDesktop3,
  imgDesktop4,
]);
const DESKTOP_MAC_IMGS = toImgDefs([imgDesktop1, imgMac2, imgMac3, imgMac4]);
const MOBILE_IMGS = toImgDefs([imgMobile1, imgMobile2, imgMobile3]);

const ImageGrid = memo(
  ({
    state,
    imgs,
    variant,
  }: {
    state: State;
    imgs: ImgDef[];
    variant: 'desktop' | 'mobile';
  }) => {
    const activeIndex = activeIndexByState[state];

    return (
      <div
        className={classNames(
          styles.img_grid,
          variant === 'desktop'
            ? styles.img_grid_desktop
            : styles.img_grid_mobile,
        )}
      >
        {imgs.map(({ src, alt }, i) => (
          <img
            key={src}
            src={src}
            alt={alt}
            className={classNames(
              styles.img,
              i === activeIndex && styles.active,
            )}
          />
        ))}
      </div>
    );
  },
);

function RouteInstructions({ extraStyles }: { extraStyles?: CSSProperties }) {
  const routeSelection = useAppSelector(selectRouteSelection);
  const desktopImgs = isMac ? DESKTOP_MAC_IMGS : DESKTOP_IMGS;

  const state: State = (() => {
    if (!routeSelection) return 'none';
    if (routeSelection.length === 1) return 'one';
    else return 'many';
  })();

  return (
    <div
      style={extraStyles}
      title="To select a route, first select a single segment as usual then hold ctrl for future selections"
    >
      <ImageGrid state={state} imgs={desktopImgs} variant="desktop" />
      <ImageGrid state={state} imgs={MOBILE_IMGS} variant="mobile" />
    </div>
  );
}
